import { emptySplitApi } from "./empty-split-api";
import type { ActiveVisitChecklistsResponse } from "./types/active-visit-checklist.type";
import {
  AppointmentChecklistState,
  AppointmentTypeChecklistItem
} from "./types/appointment-types.type";

export const appointmentChecklistApi = emptySplitApi.injectEndpoints({
  endpoints: (builder) => ({
    getChecklistItems: builder.query<AppointmentTypeChecklistItem[], number>({
      query: (appointmentTypeId) =>
        `/appointment-types/${appointmentTypeId}/checklist-items/`,
      providesTags: (_result, _error, appointmentTypeId) => [
        {
          type: "AppointmentTypeChecklistItems" as const,
          id: appointmentTypeId
        }
      ]
    }),
    addChecklistItem: builder.mutation<
      AppointmentTypeChecklistItem,
      {
        appointmentTypeId: number;
        label: string;
        is_required: boolean;
        sort_order: number;
      }
    >({
      query: ({ appointmentTypeId, ...body }) => ({
        url: `/appointment-types/${appointmentTypeId}/checklist-items/`,
        method: "POST",
        body
      }),
      invalidatesTags: (_result, _error, { appointmentTypeId }) => [
        {
          type: "AppointmentTypeChecklistItems" as const,
          id: appointmentTypeId
        }
      ]
    }),
    editChecklistItem: builder.mutation<
      AppointmentTypeChecklistItem,
      {
        appointmentTypeId: number;
        itemId: number;
        label?: string;
        is_required?: boolean;
        sort_order?: number;
        is_active?: boolean;
      }
    >({
      query: ({ appointmentTypeId, itemId, ...body }) => ({
        url: `/appointment-types/${appointmentTypeId}/checklist-items/${itemId}/`,
        method: "PATCH",
        body
      }),
      invalidatesTags: (_result, _error, { appointmentTypeId }) => [
        {
          type: "AppointmentTypeChecklistItems" as const,
          id: appointmentTypeId
        }
      ]
    }),
    deleteChecklistItem: builder.mutation<
      void,
      { appointmentTypeId: number; itemId: number }
    >({
      query: ({ appointmentTypeId, itemId }) => ({
        url: `/appointment-types/${appointmentTypeId}/checklist-items/${itemId}/`,
        method: "DELETE"
      }),
      invalidatesTags: (_result, _error, { appointmentTypeId }) => [
        {
          type: "AppointmentTypeChecklistItems" as const,
          id: appointmentTypeId
        }
      ]
    }),
    getAppointmentChecklist: builder.query<AppointmentChecklistState, number>({
      query: (appointmentId) => `/appointments/${appointmentId}/checklist/`,
      providesTags: (_result, _error, appointmentId) => [
        { type: "AppointmentChecklist" as const, id: appointmentId }
      ]
    }),
    updateAppointmentChecklist: builder.mutation<
      AppointmentChecklistState,
      {
        appointmentId: number;
        items: { id: number; is_completed: boolean }[];
      }
    >({
      query: ({ appointmentId, items }) => ({
        url: `/appointments/${appointmentId}/checklist/`,
        method: "PATCH",
        body: items
      }),
      invalidatesTags: (_result, _error, { appointmentId }) => [
        { type: "AppointmentChecklist" as const, id: appointmentId },
        { type: "ActiveVisitChecklists" as const, id: appointmentId }
      ]
    }),
    getActiveVisitChecklists: builder.query<
      ActiveVisitChecklistsResponse,
      number
    >({
      query: (patientId) => `/patients/${patientId}/active-visit-checklists/`,
      providesTags: (result, _error, patientId) =>
        result
          ? [
              ...result.appointments.map((a) => ({
                type: "ActiveVisitChecklists" as const,
                id: a.id
              })),
              {
                type: "ActiveVisitChecklists" as const,
                id: `LIST-${patientId}`
              }
            ]
          : [
              {
                type: "ActiveVisitChecklists" as const,
                id: `LIST-${patientId}`
              }
            ]
    })
  })
});

export const {
  useGetChecklistItemsQuery,
  useAddChecklistItemMutation,
  useEditChecklistItemMutation,
  useDeleteChecklistItemMutation,
  useGetAppointmentChecklistQuery,
  useUpdateAppointmentChecklistMutation,
  useGetActiveVisitChecklistsQuery
} = appointmentChecklistApi;
